import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { DotsThreeVertical, Robot, Trash, ArrowCounterClockwise } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL || '';
const GROUP_ORDER = ['Entrega clínica', 'Acessos', 'Acompanhamento', 'Administrativo', 'Extra'];

export function useTaskDetail({ appointmentId, consultationId }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const url = appointmentId ? `${API}/api/consult-tasks/appointment/${appointmentId}` : consultationId ? `${API}/api/consultations/${consultationId}/tasks` : null;
  const reload = useCallback(async () => {
    if (!url) return null;
    try { const { data } = await axios.get(url); setDetail(data); setError(''); return data; }
    catch (e) { setError(e.response?.data?.detail || 'Erro ao carregar tarefas'); return null; }
  }, [url]);
  useEffect(() => { setDetail(null); reload(); }, [reload]);
  return { detail, error, reload };
}

function TaskRow({ t, onStatus, onRemove, tid }) {
  const done = t.status === 'feita';
  const na = t.status === 'nao_aplicavel';
  return (
    <div className="flex items-center gap-1 min-h-[44px]" data-testid={`${tid}-task-${t.key}`}>
      <label className="flex items-center gap-3 flex-1 min-h-[44px] cursor-pointer min-w-0">
        <input type="checkbox" checked={done} disabled={na} onChange={e => onStatus(t, e.target.checked ? 'feita' : 'por_fazer')}
          className="w-5 h-5 shrink-0 rounded border-brand-border accent-[#657161]" data-testid={`${tid}-check-${t.key}`} />
        <span className={`text-sm leading-tight ${na ? 'line-through text-brand-placeholder' : done ? 'text-brand-text' : 'text-brand-muted'}`}>
          {t.label}
          {na && <span className="ml-1.5 text-[10px] no-underline text-brand-placeholder">(não aplicável)</span>}
        </span>
        {t.auto && (
          <span title={`Marcada automaticamente: ${t.auto_label}`} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#bbd6d5]/50 text-[#3f5a58] shrink-0" data-testid={`${tid}-auto-${t.key}`}>
            <Robot size={10} /> automático
          </span>
        )}
        {!t.auto && done && t.completed_by_name && <span className="text-[10px] text-brand-placeholder shrink-0 hidden sm:inline">{t.completed_by_name}</span>}
        {t.note && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 shrink-0" data-testid={`${tid}-note-${t.key}`}>{t.note}</span>}
      </label>
      {t.origin === 'especifica' ? (
        <button onClick={() => onRemove(t)} className="w-11 h-11 flex items-center justify-center text-brand-placeholder hover:text-red-500" aria-label="Remover tarefa" data-testid={`${tid}-remove-${t.id}`}><Trash size={16} /></button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-11 h-11 flex items-center justify-center text-brand-placeholder hover:text-brand-text" aria-label="Mais opções" data-testid={`${tid}-menu-${t.key}`}><DotsThreeVertical size={18} weight="bold" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {na
              ? <DropdownMenuItem onClick={() => onStatus(t, 'por_fazer')} data-testid={`${tid}-restore-${t.key}`}><ArrowCounterClockwise size={14} className="mr-2" /> Repor como por fazer</DropdownMenuItem>
              : <DropdownMenuItem onClick={() => onStatus(t, 'nao_aplicavel')} data-testid={`${tid}-na-${t.key}`}>Não aplicável</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export function TaskChecklist({ detail, reload, tid = 'ct', showHeaderLine = true }) {
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);
  if (!detail) return <p className="text-xs text-brand-placeholder py-4" data-testid={`${tid}-loading`}>A carregar tarefas…</p>;

  const tasks = detail.tasks || [];
  const model = tasks.filter(t => t.origin === 'modelo' && t.status !== 'arquivada');
  const specific = tasks.filter(t => t.origin === 'especifica' && t.status !== 'arquivada');
  const archived = tasks.filter(t => t.status === 'arquivada');
  const before = model.filter(t => t.group === 'antes');
  const { done, total } = detail.progress;

  const refreshBadge = () => window.dispatchEvent(new Event('nav-badges-refresh'));
  const setStatus = async (t, status) => {
    try { await axios.put(`${API}/api/consult-tasks/${t.id}/status`, { status }); await reload(); refreshBadge(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro ao atualizar tarefa'); }
  };
  const remove = async (t) => {
    const ok = await confirmDelete({ description: `Vai remover a tarefa "${t.label}" desta consulta.`, confirmLabel: 'Remover' });
    if (!ok) return;
    try { await axios.delete(`${API}/api/consult-tasks/${t.id}`); await reload(); refreshBadge(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao remover'); }
  };
  const add = async () => {
    const label = newLabel.trim();
    if (!label || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/consult-tasks`, { label, appointment_id: detail.appointment_id || undefined, consultation_id: detail.consultation_id || undefined });
      setNewLabel(''); await reload(); refreshBadge();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao adicionar'); }
    finally { setBusy(false); }
  };
  const reactivate = async (t) => {
    try { await axios.post(`${API}/api/consult-tasks/${t.id}/reactivate`); await reload(); refreshBadge(); } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao reativar'); }
  };

  return (
    <div data-testid={`${tid}-checklist`}>
      <div className="mb-4" data-testid={`${tid}-progress`}>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-brand-muted">Progresso</span>
          <span className="font-medium text-brand-text" data-testid={`${tid}-progress-text`}>{done} de {total} concluídas</span>
        </div>
        <div className="h-2 rounded-full bg-[#bbd6d5]/30 overflow-hidden"><div className="h-full bg-[#657161] transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} /></div>
      </div>

      {before.length > 0 && (
        <div className="mb-4" data-testid={`${tid}-section-antes`}>
          <h4 className="font-heading font-medium text-[11px] text-brand-muted uppercase tracking-wider mb-1">Antes da consulta</h4>
          {before.map(t => <TaskRow key={t.id} t={t} onStatus={setStatus} onRemove={remove} tid={tid} />)}
        </div>
      )}

      <div className="mb-4" data-testid={`${tid}-section-fecho`}>
        <h4 className="font-heading font-medium text-[11px] text-brand-muted uppercase tracking-wider mb-1">Fecho de consulta</h4>
        {GROUP_ORDER.map(g => {
          const items = model.filter(t => t.group === g);
          if (!items.length) return null;
          return (
            <div key={g} className="mb-2" data-testid={`${tid}-group-${g}`}>
              <p className="text-[10px] text-brand-placeholder uppercase tracking-wide mt-1">{g}</p>
              {items.map(t => <TaskRow key={t.id} t={t} onStatus={setStatus} onRemove={remove} tid={tid} />)}
            </div>
          );
        })}
      </div>

      {showHeaderLine && (
        <p className="text-[11px] text-brand-muted mb-4" data-testid={`${tid}-template-line`}>
          {detail.template_line}
          {user?.role === 'admin' && <> · <Link to="/admin/tarefas-automaticas-config" className="underline text-[#657161]" data-testid={`${tid}-edit-templates-link`}>editar modelos</Link></>}
        </p>
      )}

      <div className="border-t border-brand-border/60 pt-3" data-testid={`${tid}-specific`}>
        <h4 className="font-heading font-medium text-[11px] text-brand-muted uppercase tracking-wider mb-1">Só nesta consulta</h4>
        {specific.map(t => <TaskRow key={t.id} t={t} onStatus={setStatus} onRemove={remove} tid={tid} />)}
        <div className="flex gap-2 mt-2">
          <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="Nova tarefa" className="h-11 text-sm" data-testid={`${tid}-new-task-input`} />
          <button onClick={add} disabled={busy || !newLabel.trim()} className="px-4 h-11 rounded-xl bg-[#657161] text-white text-sm disabled:opacity-40" data-testid={`${tid}-add-task-btn`}>Adicionar</button>
        </div>
      </div>

      {archived.length > 0 && (
        <div className="mt-4 text-[11px] text-brand-muted" data-testid={`${tid}-archived`}>
          {archived.length} tarefa(s) arquivada(s):{' '}
          {archived.map(t => <button key={t.id} onClick={() => reactivate(t)} className="underline mr-2" data-testid={`${tid}-reactivate-${t.key}`}>Reativar “{t.label}”</button>)}
        </div>
      )}
    </div>
  );
}
